import { localDateInputValue } from "@topzle/locale";
import {
  ArrowLeft,
  ArrowRight,
  CalendarDays,
  KeyRound,
  Mail,
  Phone,
  ShieldCheck,
  UserRound,
} from "lucide-react";
import { type FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { trackEvent } from "../analytics";
import { api } from "../api";
import { safeReturnTo, signInPath } from "../authReturn";
import { LoadingButton } from "../components/LoadingButton";
import { OtpSafeInput, type OtpSafeState, otpUnlockDelay } from "../components/OtpSafeInput";
import { TurnstileWidget, type TurnstileWidgetHandle } from "../components/TurnstileWidget";
import { supabase } from "../supabase";
import { getUserDisplayName, useAuthSession } from "../useAuthSession";
import { useVisitorContext } from "../visitorContext";

function dateYearsAgo(date: string, years: number): string {
  const [year, month, day] = date.split("-").map(Number);
  if (!year || !month || !day) return "";
  const targetYear = year - years;
  const lastDay = new Date(Date.UTC(targetYear, month, 0)).getUTCDate();
  return `${targetYear.toString().padStart(4, "0")}-${month
    .toString()
    .padStart(2, "0")}-${Math.min(day, lastDay).toString().padStart(2, "0")}`;
}

function ageAtDate(dateOfBirth: string, today: string): number | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateOfBirth)) return null;
  const [birthYear, birthMonth, birthDay] = dateOfBirth.split("-").map(Number);
  const [year, month, day] = today.split("-").map(Number);
  if (!birthYear || !birthMonth || !birthDay || !year || !month || !day) return null;
  return (
    year - birthYear - (month < birthMonth || (month === birthMonth && day < birthDay) ? 1 : 0)
  );
}

export function OnboardingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const requestedReturnTo = (location.state as { returnTo?: unknown } | null)?.returnTo;
  const returnTo = safeReturnTo(
    requestedReturnTo ?? new URLSearchParams(location.search).get("returnTo"),
  );
  const { session, loading } = useAuthSession();
  const visitor = useVisitorContext();
  const today = localDateInputValue(
    undefined,
    visitor.timeZone ? { timeZone: visitor.timeZone } : {},
  );
  const oldestDateOfBirth = dateYearsAgo(today, 120);
  const youngestDateOfBirth = dateYearsAgo(today, 13);
  const challengeRef = useRef<TurnstileWidgetHandle>(null);
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneCode, setPhoneCode] = useState("");
  const [phoneOtpState, setPhoneOtpState] = useState<OtpSafeState>("idle");
  const [phoneStep, setPhoneStep] = useState<"phone" | "code">("phone");
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [phonePending, setPhonePending] = useState(false);
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [guardianEmail, setGuardianEmail] = useState("");
  const [parentalConsentAcknowledged, setParentalConsentAcknowledged] = useState(false);
  const [pending, setPending] = useState(false);
  const [statusLoading, setStatusLoading] = useState(true);
  const [dateOfBirthLocked, setDateOfBirthLocked] = useState(false);
  const [message, setMessage] = useState("");
  const receiveChallenge = useCallback((token: string | null) => setCaptchaToken(token), []);
  const hasVerifiedPhone = phoneVerified && Boolean(phone.trim());
  const age = ageAtDate(dateOfBirth, today);
  const isTeen = age !== null && age >= 13 && age < 18;

  useEffect(() => {
    if (loading) return;
    if (!session) {
      navigate(signInPath(returnTo), { replace: true });
      return;
    }
    let active = true;
    setStatusLoading(true);
    setMessage("");
    void api
      .onboardingStatus()
      .then((status) => {
        if (!active) return;
        const linkedPhone = typeof status.phone === "string" ? status.phone.trim() : "";
        setPhone((current) => linkedPhone || current || visitor.callingCode || "");
        setPhoneVerified(status.phoneVerified && Boolean(linkedPhone));
        if (status.onboardingCompleted) {
          navigate(returnTo, { replace: true });
          return;
        }
        if (status.dateOfBirthSet) {
          setDateOfBirthLocked(true);
          setMessage(
            "Your date of birth is already locked. Contact support if your account setup is incomplete.",
          );
          return;
        }
        setDisplayName(status.displayName || getUserDisplayName(session.user));
        setEmail(status.email || session.user.email || "");
      })
      .catch((error) => {
        if (active)
          setMessage(error instanceof Error ? error.message : "Your profile could not be loaded.");
      })
      .finally(() => {
        if (active) setStatusLoading(false);
      });
    return () => {
      active = false;
    };
  }, [loading, navigate, returnTo, session, visitor.callingCode]);

  const requestPhoneCode = async () => {
    setMessage("");
    setPhonePending(true);
    try {
      if (!/^\+[1-9]\d{7,14}$/.test(phone.trim()))
        throw new Error("Enter a full phone number with country code, for example +919876543210.");
      if (!captchaToken) throw new Error("Complete the security check first.");
      const result = await api.requestPhoneOtp({
        phone: phone.trim(),
        turnstileToken: captchaToken,
      });
      setPhoneStep("code");
      setPhoneCode("");
      setPhoneOtpState("idle");
      setMessage(`We sent a code to ${result.destination}.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "The code could not be sent.");
      challengeRef.current?.reset();
    } finally {
      setPhonePending(false);
    }
  };

  const verifyAndLinkPhone = async () => {
    setMessage("");
    setPhonePending(true);
    try {
      if (!/^\d{6}$/.test(phoneCode))
        throw new Error("Enter the six-digit code from your text message.");
      setPhoneOtpState("validating");
      const linked = await api.linkVerifiedPhone({ phone: phone.trim(), code: phoneCode });
      if (!supabase) throw new Error("Connect Supabase before linking your phone.");
      const { error: refreshError } = await supabase.auth.refreshSession();
      if (refreshError) throw refreshError;
      const status = await api.onboardingStatus();
      if (!status.phoneVerified || !status.phone)
        throw new Error(
          "Your phone was linked, but the account session did not refresh. Try again.",
        );
      setPhoneOtpState("success");
      await otpUnlockDelay();
      setPhone(status.phone || linked.phone);
      setPhoneVerified(true);
      setMessage("Phone verified. You can finish your profile now.");
      if (dateOfBirthLocked) navigate(returnTo, { replace: true });
    } catch (error) {
      setPhoneOtpState("error");
      setMessage(error instanceof Error ? error.message : "Your phone could not be linked.");
    } finally {
      setPhonePending(false);
    }
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!session || !supabase) return;
    if (!hasVerifiedPhone) {
      setMessage("Verify your phone before finishing setup.");
      return;
    }
    if (age === null || age < 13 || age > 120) {
      setMessage("Topzle accounts are available from age 13. Enter a valid date of birth.");
      return;
    }
    if (isTeen && (!guardianEmail.trim() || !parentalConsentAcknowledged)) {
      setMessage("Enter a parent or guardian email and confirm they will review the account.");
      return;
    }
    setPending(true);
    setMessage("");
    try {
      const result = await api.completeOnboarding({
        displayName,
        email,
        dateOfBirth,
        acceptedTerms: true,
        acceptedPrivacy: true,
        ...(isTeen
          ? {
              guardianEmail: guardianEmail.trim(),
              parentalConsentAcknowledged: true as const,
            }
          : {}),
      });
      const { error } = await supabase.auth.updateUser({
        data: { display_name: displayName },
      });
      if (error) setMessage(`Profile saved. Display name sync is pending: ${error.message}`);
      await supabase.auth.refreshSession();
      trackEvent("sign_up", { method: session.user.email ? "email" : "phone" });
      if (result.parentalConsentRequired) {
        setMessage(
          result.parentalConsentEmailSent
            ? "Your account is ready and you can play. We emailed your parent or guardian; age-aware chat remains limited while their review is pending."
            : "Your account is ready and you can play, but the guardian email could not be sent. Chat safety limits remain on; retry from account settings.",
        );
        window.setTimeout(() => navigate(returnTo, { replace: true }), 2_400);
      } else navigate(returnTo, { replace: true });
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Your profile could not be saved.");
    } finally {
      setPending(false);
    }
  };

  if (loading || statusLoading || !session)
    return <section className="empty-state">Loading your account…</section>;

  return (
    <section className="auth-page onboarding-page">
      <form className="auth-card onboarding-card" onSubmit={(event) => void submit(event)}>
        <span className="icon-tile">
          <ShieldCheck />
        </span>
        <span className="eyebrow">ONE LAST STEP</span>
        <h1>Make it yours.</h1>
        <p>
          We use your age only to switch on the right safety protections. It is never shown in a
          room.
        </p>
        {!hasVerifiedPhone ? (
          <div className="onboarding-phone">
            <div className="onboarding-phone-heading">
              {phoneStep === "phone" ? <Phone /> : <KeyRound />}
              <div>
                <strong>Verify your mobile number</strong>
                <small>Your number stays private and becomes your secure sign-in method.</small>
              </div>
            </div>
            {phoneStep === "phone" ? (
              <>
                <label>
                  <span>
                    <Phone /> Mobile number
                  </span>
                  <input
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    value={phone}
                    onChange={(event) =>
                      setPhone(event.target.value.replace(/[^\d+]/g, "").slice(0, 16))
                    }
                    placeholder="+919876543210"
                    disabled={phonePending}
                    required
                  />
                </label>
                <TurnstileWidget
                  ref={challengeRef}
                  action="phone_auth"
                  onToken={receiveChallenge}
                />
                <LoadingButton
                  type="button"
                  className="button button-secondary wide"
                  onClick={() => void requestPhoneCode()}
                  loading={phonePending}
                  loadingLabel="Sending code…"
                  disabled={!captchaToken}
                >
                  Send verification code <ArrowRight />
                </LoadingButton>
              </>
            ) : (
              <>
                <label htmlFor="onboarding-phone-code">
                  <span>
                    <KeyRound /> Verification code
                  </span>
                  <OtpSafeInput
                    id="onboarding-phone-code"
                    value={phoneCode}
                    onChange={(value) => {
                      setPhoneCode(value);
                      setPhoneOtpState("idle");
                    }}
                    state={phoneOtpState}
                    disabled={phonePending}
                  />
                </label>
                <LoadingButton
                  type="button"
                  className="button button-secondary wide"
                  onClick={() => void verifyAndLinkPhone()}
                  loading={phonePending}
                  loadingLabel="Verifying…"
                  disabled={phoneCode.length !== 6}
                >
                  Verify phone <ArrowRight />
                </LoadingButton>
                <button
                  type="button"
                  className="text-link"
                  disabled={phonePending}
                  onClick={() => {
                    setPhoneStep("phone");
                    setPhoneCode("");
                    setPhoneOtpState("idle");
                    setMessage("");
                    challengeRef.current?.reset();
                  }}
                >
                  <ArrowLeft /> Change number or resend
                </button>
              </>
            )}
          </div>
        ) : (
          <div className="onboarding-phone verified">
            <div className="onboarding-phone-heading">
              <ShieldCheck />
              <div>
                <strong>Mobile number verified</strong>
                <small>
                  Your private sign-in number ends in {phone.slice(-4)}. It is never shown to other
                  players.
                </small>
              </div>
            </div>
          </div>
        )}
        <label>
          <span>
            <UserRound /> Display name
          </span>
          <input
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value.slice(0, 40))}
            autoComplete="name"
            placeholder="How friends will see you"
            minLength={2}
            required
            disabled={pending || dateOfBirthLocked}
          />
        </label>
        <label>
          <span>
            <Mail /> Email
          </span>
          <input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoComplete="email"
            placeholder="you@example.com"
            required
            disabled={pending || dateOfBirthLocked}
          />
          <small>Used for game invitations, account notices and recovery.</small>
        </label>
        <label>
          <span>
            <CalendarDays /> Date of birth
          </span>
          <input
            type="date"
            value={dateOfBirth}
            onChange={(event) => setDateOfBirth(event.target.value)}
            min={oldestDateOfBirth}
            max={youngestDateOfBirth}
            required
            disabled={pending || dateOfBirthLocked}
          />
          <small>
            Topzle accounts are available from age 13. Check this carefully; it can be saved only
            once.
          </small>
        </label>
        {isTeen && (
          <div className="parental-consent-panel">
            <div>
              <ShieldCheck />
              <span>
                <strong>A parent or guardian must approve</strong>
                <small>
                  You can play public and private games now. Free-text chat stays limited until an
                  adult reviews the account through a secure email link.
                </small>
              </span>
            </div>
            <label>
              <span>
                <Mail /> Parent or guardian email
              </span>
              <input
                type="email"
                value={guardianEmail}
                onChange={(event) => setGuardianEmail(event.target.value.slice(0, 254))}
                autoComplete="email"
                placeholder="guardian@example.com"
                required
                disabled={pending || dateOfBirthLocked}
              />
            </label>
            <label className="consent-check">
              <input
                type="checkbox"
                checked={parentalConsentAcknowledged}
                onChange={(event) => setParentalConsentAcknowledged(event.target.checked)}
                required
                disabled={pending || dateOfBirthLocked}
              />
              <span>
                I confirm this belongs to my parent or legal guardian, and understand that they must
                approve before I can use Topzle’s social features.
              </span>
            </label>
          </div>
        )}
        <label className="consent-check">
          <input
            type="checkbox"
            checked={accepted}
            onChange={(event) => setAccepted(event.target.checked)}
            required
            disabled={pending || dateOfBirthLocked}
          />
          <span>
            I agree to the <Link to="/terms">Terms</Link> and have read the{" "}
            <Link to="/privacy">Privacy Policy</Link>.
          </span>
        </label>
        <LoadingButton
          type="submit"
          className="button button-primary wide"
          loading={pending}
          loadingLabel="Saving your profile…"
          disabled={
            pending ||
            phonePending ||
            !hasVerifiedPhone ||
            !accepted ||
            dateOfBirthLocked ||
            age === null ||
            age < 13 ||
            (isTeen && (!guardianEmail.trim() || !parentalConsentAcknowledged))
          }
        >
          Finish setup <ArrowRight />
        </LoadingButton>
        {message && (
          <div className="status-message" role="status">
            {message}
          </div>
        )}
      </form>
    </section>
  );
}
